import { Typography } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import Fab from '@material-ui/core/Fab';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import PropTypes from 'prop-types';
import React from 'react';
import { Redirect, withRouter } from 'react-router-dom';
import BasicBackground from '../BasicBackground';
import Errored from '../Errored';
import Legal from '../Legal';
import MinorButton from '../MinorButton';
import { authenticationService } from '../services/security/authenticationService';
import { globalStyles } from '../style/gstheme';
import { MAX_PASSWORD_LENGTH, MAX_USERNAME_LENGTH, MANAGED_MODE } from '../utils/constants';
import { processHashLocation, validatePassword, validateUsername } from '../utils/utils';
import AccessHeader from './AccessHeader';

const endpoint = process.env.REACT_APP_API_URL || "";

// Because of Chrome autofill we use this approach
// https://codedaily.io/tutorials/69/Animated-Input-Label-with-Chrome-Autofill-Detection-in-React

const styles = () => {
    return Object.assign({
        forgotten: {
            fontSize: 12,
            color: '#0066cc',
            '&:hover': {
                cursor: 'pointer'
            }
        },
        login: {
            textAlign: 'center',
            height: '600px',
            position: 'fixed',
            top: 'calc(50% - 280px)',
            left: 'calc(50% - 192px)',
            width: 384,
            padding: 20,
            borderRadius: 5,
            backgroundColor: 'white',
        },
        loginText: {
            '&:-webkit-autofill': {
                "animation-name": 'onAutoFillStart',
                transition: 'background-color 50000s ease-in-out 0s'
            },
            '&:not(:-webkit-autofill) ': {
                "animation-name": 'onAutoFillCancel'
            }
        }
    }, globalStyles);
};

class Login extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            offerLoginType: window.Graphologi.sso === true,
            loginType: undefined,
            username: "",
            password: "",
            legal: false,
            status: null,
            isErrored: null,
            accessing: false,
            nameAutofill: false,
            passwordAutofill: false
        };
        this.loginRef = React.createRef();
        this.textInput = React.createRef();
        this.passwordInput = React.createRef();
    }

    componentDidMount() {
        this.textInput && this.textInput.current && this.textInput.current.addEventListener('animationstart', (e) => {
            if (e.animationName === "mui-auto-fill") {
                this.setState({ nameAutofill: true });
            }
        });
        this.passwordInput.current && this.passwordInput.current.addEventListener('animationstart', (e) => {
            if (e.animationName === "mui-auto-fill") {
                this.setState({ passwordAutofill: true });
            }
        });
    }

    login = () => {
        this.setState({ accessing: true }, () => {
            const { username, password } = this.state;
            authenticationService.login(username, password)
                .then((data) => {
                    const paramsMap = processHashLocation(this.props.location);
                    const status = paramsMap.get("status");
                    const firstVisit = status && status === "firstvisit";
                    if (status && status === "ignore") this.props.ignoreSubscription();
                    this.props.setUser(data, firstVisit, true);
                })
                .catch((e) => {
                    const message = [500, 501, 502, 503, 504].includes(e.status) ?
                        e.status === 503 && e.message.includes("Maintenance window") ?
                            `Graphologi is in maintenance mode until ${(new Date(e.endTime)).toLocaleString()}.\nPlease try again after then.`
                            :
                            "The system is unable to log in at the moment. Please try again later."
                        :
                        e.status === 429 ?
                            "You have made too many attempts to log in. Please try again later."
                            :
                            e.message;
                    this.setState({ isErrored: { message }, accessing: false });
                });
        });
    };

    clearError = () => {
        this.setState({ isErrored: null }, () => this.textInput && this.textInput.current && this.textInput.current.focus());
    };

    handleChange = (event) => {
        const t = event.target;
        this.setState({ [t.name]: t.value, nameAutofill: false, passwordAutofill: false });
    };

    showLegal = () => this.setState({ legal: true });

    exitLegal = () => this.setState({ legal: false });

    goToSignup = () => this.props.history.push("/signup");

    goToForgottenUsername = () => this.props.history.push("/forgottenusername");

    goToForgottenPassword = () => this.props.history.push("/forgotten");

    ssoLogin = () => {
        if (this.props.initialURL) {
            window.location.href = endpoint + "/authenticate/sso-login?RelayState=" + encodeURIComponent(this.props.initialURL);
        }
        else {
            window.location.href = endpoint + "/authenticate/sso-login";
        }
    };

    selectLoginType = () => this.setState({ loginType: "username" });

    render() {

        const { username, password, isErrored, legal, accessing, nameAutofill, passwordAutofill, offerLoginType, loginType } = this.state;
        const { classes, user } = this.props;
        const validPassword = validatePassword(password);
        const validUsername = validateUsername(username);
        // If logged in already redirect to home page
        if (user) return <Redirect to="/" />;
        const disabled = nameAutofill && passwordAutofill ?
            false
            :
            username === "" || password === "" || !validPassword || !validUsername || accessing;

        return (
            <BasicBackground>
                <Grid container spacing={0} direction="row" className={classes.login} >
                    <Grid item xs={12}>
                        {window.Graphologi.title &&
                            <Typography paragraph className={classes.mainHeading}>{window.Graphologi.title}</Typography>
                        }
                        <div ref={this.loginRef}>
                            <AccessHeader title="Login" />
                        </div>
                    </Grid>
                    {offerLoginType && !loginType ?
                        <React.Fragment>
                            <Grid item xs={12}>
                                <div style={{ margin: '20px 20px 40px 20px' }}>
                                    <Fab
                                        className={classes.button}
                                        variant="extended"
                                        color="secondary"
                                        disabled={accessing}
                                        data-test="sso-login"
                                        style={{ width: '100%' }}
                                        onClick={this.ssoLogin}>Login by Single Sign-On</Fab>
                                </div>
                            </Grid>
                            <Grid item xs={12}>
                                <MinorButton disabled={accessing} data-test="username-login" onClick={this.selectLoginType}>Login by Username/Password</MinorButton>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography className={classes.fromGS}>Graphologi from <a style={{ color: '#0066CC' }} rel='noreferrer' target="_blank" href="https://graphifi.com">Graphifi</a></Typography>
                            </Grid>
                        </React.Fragment>
                        :
                        <React.Fragment>
                            <Grid item xs={12}>
                                <div className={classes.loginEntry}>
                                    <Typography className={classes.loginSubHeading}>Username</Typography>
                                    <TextField className={classes.loginText}
                                        FormHelperTextProps={{
                                            "data-test": "username-helper-text"
                                        }}
                                        margin="dense"
                                        data-test="username"
                                        id="username"
                                        name="username"
                                        value={username}
                                        inputRef={this.textInput}
                                        autoFocus={true}
                                        error={username.length > 0 && !validUsername}
                                        variant="outlined"
                                        placeholder="Username"
                                        helperText={username.length > 0 && !validUsername ? "Username not valid." : ""}
                                        fullWidth
                                        onKeyPress={(ev) => {
                                            if (!disabled && ev.charCode === 13) {
                                                this.login();
                                                ev.preventDefault();
                                                ev.stopPropagation();
                                            }
                                        }}
                                        onChange={this.handleChange}
                                        InputProps={{
                                            className: classes.loginInput,
                                            inputProps: {
                                                className: classes.loginInput,
                                                maxLength: MAX_USERNAME_LENGTH
                                            }
                                        }}
                                    />
                                    <Typography className={classes.loginSubHeading} style={{ marginTop: 12 }}>Password</Typography>
                                    <TextField className={classes.loginText}
                                        FormHelperTextProps={{
                                            "data-test": "password-helper-text"
                                        }}
                                        margin="dense"
                                        data-test="password"
                                        inputRef={this.passwordInput}
                                        id="password"
                                        name="password"
                                        type="password"
                                        error={password.length > 0 && !validPassword}
                                        placeholder="Password"
                                        helperText={password.length > 0 && !validPassword ? "Password not valid." : ""}
                                        value={password}
                                        variant="outlined"
                                        fullWidth
                                        onChange={this.handleChange}
                                        onKeyPress={(ev) => {
                                            if (!disabled && ev.charCode === 13) {
                                                this.login();
                                                ev.preventDefault();
                                                ev.stopPropagation();
                                            }
                                        }}
                                        InputProps={{
                                            classes: {
                                                root: classes.loginInput
                                            },
                                            inputProps: {
                                                maxLength: MAX_PASSWORD_LENGTH
                                            }
                                        }}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={6} style={{ paddingTop: 12 }}>
                                {window.Graphologi.mode !== MANAGED_MODE &&
                                    <MinorButton disabled={accessing} data-test="signup" onClick={this.goToSignup}>Sign Up</MinorButton>
                                }
                            </Grid>
                            <Grid item xs={6} style={{ padding: '10px 20px' }}>
                                <Fab
                                    data-test="login"
                                    disabled={disabled}
                                    variant="extended"
                                    color="secondary"
                                    className={classes.button}
                                    style={{ width: '100%' }}
                                    onClick={() => this.login()}>Log in</Fab>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography style={{ marginTop: 20 }} data-test="forgotten-username">
                                    <span className={classes.forgotten} onClick={this.goToForgottenUsername}>Forgotten Username</span>
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography style={{ marginTop: 20 }} data-test="forgotten-password">
                                    <span className={classes.forgotten} onClick={this.goToForgottenPassword}>Forgotten Password</span>
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <div style={{ height: 20 }}>
                                    <Divider light />
                                </div>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography className={classes.fromGS}>Graphologi from <a style={{ color: '#0066CC' }} rel='noreferrer' target="_blank" href="https://graphifi.com">Graphifi</a></Typography>
                            </Grid>
                            {window.Graphologi.mode !== MANAGED_MODE &&
                                <Grid item xs={6}>
                                    <Typography data-test="ts-and-cs" className={classes.legalLink} onClick={this.showLegal}>Terms and Conditions</Typography>
                                    <Legal open={legal} exitLegal={this.exitLegal} />
                                </Grid>
                            }
                        </React.Fragment>
                    }
                </Grid>
                {isErrored && <Errored data={isErrored} clearError={this.clearError} />}
            </BasicBackground>
        );
    }
}

Login.propTypes = {
    setUser: PropTypes.func.isRequired,
    location: PropTypes.object,
    user: PropTypes.object,
    ignoreSubscription: PropTypes.func.isRequired,
    classes: PropTypes.object,
    history: PropTypes.object
};

export default withRouter(withStyles(styles)(Login));